import type { StateCreator } from 'zustand'

// 2D/3D annotations layered on top of the model, independent from the
// IFC structure. Stored as a flat array; renderer maps each entry to
// a CSS2DObject (text), a Three.Line (leader/arrow), or a custom
// Three.Mesh (revision cloud).

export type AnnotationKind =
  | 'text'
  | 'leader'
  | 'cloud'
  | 'rect'
  | 'coord'

// Annotation placement mode driven by the ribbon. While set, the next
// canvas click(s) place the annotation. null = idle (default).
export type AnnotationPlaceMode =
  | null
  | 'text'
  | 'leader'
  | 'cloud'
  | 'rect'
  | 'coord'

export interface BaseAnnotation {
  id: string
  kind: AnnotationKind
  // World-space anchor point. Text floats here; leader's tail; cloud's
  // bbox centre.
  anchor: [number, number, number]
  // RGB hex like '#f59e0b'. Optional; renderer falls back to amber.
  color?: string
}

export interface TextAnnotation extends BaseAnnotation {
  kind: 'text'
  text: string
}

export interface LeaderAnnotation extends BaseAnnotation {
  kind: 'leader'
  // Tail end (where the arrow head sits). Anchor is the start.
  tail: [number, number, number]
  text?: string
}

export interface CloudAnnotation extends BaseAnnotation {
  kind: 'cloud'
  // Two opposite corners in world space defining the rectangular bbox
  // around which the cloud "bumps" are drawn.
  cornerA: [number, number, number]
  cornerB: [number, number, number]
  // Plane normal in world space: the cloud is drawn in the plane
  // through `anchor` perpendicular to this. Defaults to +Y if absent
  // (legacy XZ behaviour).
  planeNormal?: [number, number, number]
  // Optional revision tag drawn in the corner (e.g. "R3").
  tag?: string
}

export interface RectAnnotation extends BaseAnnotation {
  kind: 'rect'
  cornerA: [number, number, number]
  cornerB: [number, number, number]
  // Plane normal in world space (cf. CloudAnnotation).
  planeNormal?: [number, number, number]
  // Optional fill (translucent). When false, only the outline shows.
  filled?: boolean
}

export interface CoordAnnotation extends BaseAnnotation {
  kind: 'coord'
  // Coordinate body is auto-formatted from the anchor + the optional
  // reference origin captured at placement time. We store the
  // reference snapshot so the label stays stable even if the user
  // later changes the live origin.
  reference?: [number, number, number] | null
  units: 'mm' | 'cm' | 'm' | 'km'
  // Optional offset (world space) from the anchor to where the label
  // sits. The leader line connects anchor → anchor+offset. When
  // absent the renderer picks a default in the camera-up direction
  // sized to the visible scene.
  labelOffset?: [number, number, number]
}

export type Annotation =
  | TextAnnotation
  | LeaderAnnotation
  | CloudAnnotation
  | RectAnnotation
  | CoordAnnotation

export interface AnnotationsSlice {
  annotations: Annotation[]
  annotationPlaceMode: AnnotationPlaceMode
  // While placing a leader/cloud the user clicks twice. Stash the
  // first click here; the renderer reads it to draw a rubber-band
  // preview, the placement handler reads it on second click to commit.
  annotationDraftFirstPoint: [number, number, number] | null

  addAnnotation: (a: Annotation) => void
  updateAnnotation: (id: string, patch: Partial<Annotation>) => void
  removeAnnotation: (id: string) => void
  clearAnnotations: () => void
  setAnnotationPlaceMode: (mode: AnnotationPlaceMode) => void
  setAnnotationDraftFirstPoint: (
    p: [number, number, number] | null,
  ) => void
}

export const createAnnotationsSlice: StateCreator<AnnotationsSlice> = (
  set,
) => ({
  annotations: [],
  annotationPlaceMode: null,
  annotationDraftFirstPoint: null,

  addAnnotation: (a) =>
    set((s) => ({ annotations: [...s.annotations, a] })),
  updateAnnotation: (id, patch) =>
    set((s) => ({
      annotations: s.annotations.map((a) =>
        a.id === id ? ({ ...a, ...patch } as Annotation) : a,
      ),
    })),
  removeAnnotation: (id) =>
    set((s) => ({ annotations: s.annotations.filter((a) => a.id !== id) })),
  clearAnnotations: () => set({ annotations: [] }),
  setAnnotationPlaceMode: (mode) =>
    set({ annotationPlaceMode: mode, annotationDraftFirstPoint: null }),
  setAnnotationDraftFirstPoint: (p) => set({ annotationDraftFirstPoint: p }),
})

export function makeAnnotationId(): string {
  return `anno_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`
}
